import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  MapPin,
  CheckCircle,
  Settings,
  LayoutGrid,
  List,
  Printer,
  QrCode
} from 'lucide-react'
import apiClient from '@/api/client'
import { cn } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import { TableForm } from '@/components/forms/TableForm'
import { TableQrSheet } from '@/components/admin/tables/TableQrSheet'
import { TableTentSheet } from '@/components/admin/tables/TableTentSheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { DiningTable } from '@/types'
import { TableLayoutBuilder } from '@/components/tables/TableLayoutBuilder'
import { buildFloorTabs, mergeFloorList } from '@/lib/managedFloors'
import { invalidateTables } from '@/lib/tablesCache'
import { parseFloorLayouts } from '@/lib/tableMapSizing'
import {
  formatDuration,
  minutesSince,
  turnoverStatus,
  TURNOVER_STATUS_STYLES,
} from '@/lib/tableTurnover'

// The live turnover timer anchors on when the party was seated: the open-tab
// timestamp when present, else the order's created_at (dine-in orders rung up
// via CreateOrder have a NULL table_opened_at).
type TableWithOrder = DiningTable & {
  current_order?: { created_at?: string | null; table_opened_at?: string | null }
}

function occupancyStartOf(table: DiningTable): string | null {
  const order = (table as TableWithOrder).current_order
  return order?.table_opened_at ?? order?.created_at ?? null
}

type ViewMode = 'list' | 'table-form' | 'layout'

export function AdminTableManagement() {
  const [viewMode, setViewMode] = useState<ViewMode>('layout')
  const [searchTerm, setSearchTerm] = useState('')
  const [editingTable, setEditingTable] = useState<DiningTable | null>(null)
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'occupied'>('all')
  const [selectedFloor, setSelectedFloor] = useState<string>('Main Floor')
  const [pendingFloors, setPendingFloors] = useState<string[]>([])
  const [deleteTableOpen, setDeleteTableOpen] = useState(false)
  const [pendingDeleteTable, setPendingDeleteTable] = useState<DiningTable | null>(null)
  const [qrTable, setQrTable] = useState<DiningTable | null>(null)
  const [tentsOpen, setTentsOpen] = useState(false)

  // One shared clock drives every live turnover timer so all occupied tiles
  // re-render together (rather than one interval per tile). 30s cadence is
  // plenty for a minute-resolution stopwatch.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(id)
  }, [])

  const queryClient = useQueryClient()

  // While the operator is mid-edit in the layout builder, a live SSE event must
  // NOT refetch ['tables-summary'] — that refetch drives the builder's floor and
  // would reseed over unsaved drags. The builder guards against the clobber
  // itself (see TableLayoutBuilder's reseed effect), but suppressing the refetch
  // here also avoids wasted network churn during editing.
  const layoutDirtyRef = useRef(false)
  const handleLayoutDirtyChange = useCallback((d: boolean) => {
    layoutDirtyRef.current = d
  }, [])
  const viewModeRef = useRef<ViewMode>('layout')

  // Live table state — refresh the floor + list the instant an order opens,
  // closes, transfers, or pays out. Replaces purely poll-based freshness.
  const handleStreamEvent = useCallback(
    (ev: DashboardStreamEvent) => {
      if (
        ev.type === 'table_status_changed' ||
        ev.type === 'table_changed' ||
        ev.type === 'order_created' ||
        ev.type === 'order_completed' ||
        ev.type === 'order_cancelled' ||
        ev.type === 'payment'
      ) {
        const editingLayout = viewModeRef.current === 'layout' && layoutDirtyRef.current
        if (!editingLayout) {
          queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
        }
        queryClient.invalidateQueries({ queryKey: ['tables'] })
      }
    },
    [queryClient],
  )
  useDashboardStream({ onEvent: handleStreamEvent })

  // Keep the SSE gate in sync with the current view. Leaving the layout builder
  // clears the dirty flag so background refetches resume immediately.
  useEffect(() => {
    viewModeRef.current = viewMode
    if (viewMode !== 'layout') layoutDirtyRef.current = false
  }, [viewMode])

  const { data: allTables = [], isLoading } = useQuery({
    queryKey: ['tables-summary'],
    // `?? []`: the `= []` destructuring default only fires for undefined, so a
    // JSON `null` body would reach allTables.map() untouched.
    queryFn: () => apiClient.getTables().then(res => res.data ?? [])
  })
  const { data: floorSettingRes } = useQuery({
    queryKey: ['settings', 'managed_floors'],
    queryFn: () => apiClient.getSetting('managed_floors'),
  })

  // Per-floor canvas dimensions, stored as { [floorName]: {width, height} }.
  // Each floor opted out of the legacy 920x560 cage gets the entry it
  // configured here; missing entries fall back to a saner default in the
  // builder.
  const { data: floorLayoutsRes } = useQuery({
    queryKey: ['settings', 'floor_layouts'],
    queryFn: () => apiClient.getSetting('floor_layouts'),
  })
  const floorLayouts = useMemo(
    () => parseFloorLayouts(floorLayoutsRes?.data),
    [floorLayoutsRes?.data],
  )

  const floorLayoutsMutation = useMutation({
    mutationFn: (next: Record<string, { width: number; height: number }>) =>
      apiClient.updateSetting('floor_layouts', next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'floor_layouts'] })
    },
    onError: (error: unknown) => {
      toastHelpers.apiError('Resize floor', error)
    },
  })

  const handleResizeFloor = async (
    name: string,
    dims: { width: number; height: number }
  ) => {
    const next = { ...floorLayouts, [name]: dims }
    await floorLayoutsMutation.mutateAsync(next)
    toastHelpers.success('Floor resized', `${name} → ${dims.width} × ${dims.height}px`)
  }

  const deleteTableMutation = useMutation({
    mutationFn: ({ id }: { id: string; tableNumber: string }) => apiClient.deleteTable(id),
    onSuccess: (_, { tableNumber }) => {
      queryClient.invalidateQueries({ queryKey: ['tables'] })
      queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
      toastHelpers.apiSuccess('Delete', `Table ${tableNumber}`)
    },
    onError: (error: any) => {
      toastHelpers.apiError('Delete table', error)
    }
  })

  const handleFormSuccess = () => {
    setViewMode('list')
    setEditingTable(null)
  }

  const handleCancelForm = () => {
    setViewMode('list')
    setEditingTable(null)
  }

  const handleDeleteTable = (table: DiningTable) => {
    const occupied = table.has_active_order ?? table.is_occupied
    if (occupied) {
      toastHelpers.warning(
        'Cannot Delete Table',
        `Table ${table.table_number} is currently occupied. Please clear the table first.`
      )
      return
    }

    setPendingDeleteTable(table)
    setDeleteTableOpen(true)
  }

  const confirmDeleteTable = () => {
    if (pendingDeleteTable) {
      deleteTableMutation.mutate({ 
        id: pendingDeleteTable.id.toString(), 
        tableNumber: pendingDeleteTable.table_number 
      })
    }
    setDeleteTableOpen(false)
    setPendingDeleteTable(null)
  }

  const floors = useMemo(() => {
    const tableLocations = (allTables as DiningTable[]).map((t) => t.location || 'Main Floor')
    return buildFloorTabs(floorSettingRes?.data, tableLocations)
  }, [allTables, floorSettingRes?.data])

  const effectiveFloors = useMemo(() => mergeFloorList(floors, pendingFloors), [floors, pendingFloors])

  useEffect(() => {
    if (pendingFloors.length === 0) return
    setPendingFloors((prev) =>
      prev.filter((name) => !floors.some((f) => f.toLowerCase() === name.toLowerCase()))
    )
  }, [floors, pendingFloors.length])

  useEffect(() => {
    if (!effectiveFloors.includes(selectedFloor)) {
      setSelectedFloor(effectiveFloors[0] || 'Main Floor')
    }
  }, [effectiveFloors, selectedFloor])

  const filteredTables = useMemo(() => {
    return (allTables as DiningTable[]).filter((table) => {
      const occupied = table.has_active_order ?? table.is_occupied
      const statusMatch =
        filterStatus === 'all' ? true : filterStatus === 'occupied' ? occupied : !occupied
      const query = searchTerm.trim().toLowerCase()
      const text = `${table.table_number} ${table.location ?? ''}`.toLowerCase()
      return statusMatch && (!query || text.includes(query))
    })
  }, [allTables, filterStatus, searchTerm])

  const stats = {
    total: (allTables as DiningTable[]).length,
    available: (allTables as DiningTable[]).filter((t) => !(t.has_active_order ?? t.is_occupied)).length,
    occupied: (allTables as DiningTable[]).filter((t) => t.has_active_order ?? t.is_occupied).length,
  }

  const saveLayoutMutation = useMutation({
    mutationFn: async (tables: Array<DiningTable & { map_x: number; map_y: number; map_w: number; map_h: number; map_rotation: number; shape: string }>) => {
      // Send each table's updated_at as If-Match so a concurrent admin's
      // edits don't get silently clobbered. A single 409 aborts the bulk
      // save; we surface a clear "reload" toast in onError below.
      const conflicts: string[] = []
      await Promise.all(
        tables.map(async (t) => {
          try {
            await apiClient.updateTable(
              t.id,
              {
                map_x: t.map_x,
                map_y: t.map_y,
                map_w: t.map_w,
                map_h: t.map_h,
                map_rotation: t.map_rotation,
                shape: t.shape,
                location: t.location || selectedFloor,
                zone: null,
              },
              { ifMatch: t.updated_at || null }
            )
          } catch (err) {
            const e = err as { status?: number; code?: string }
            if (e?.status === 409 && e?.code === 'version_mismatch') {
              conflicts.push(t.table_number)
              return
            }
            throw err
          }
        })
      )
      if (conflicts.length > 0) {
        const err = new Error(
          `These tables were edited by someone else: ${conflicts.join(', ')}. Reload and try again.`
        ) as Error & { code?: string }
        err.code = 'version_mismatch'
        throw err
      }

      // Return the freshly-bumped updated_at etags so the builder can refresh
      // its rows IN PLACE (without discarding positions). Without this, a second
      // consecutive save would resend the now-stale pre-save updated_at and 409
      // against the very rows we just wrote — the "save only works once" bug.
      // Genuine concurrent edits are still caught: our next If-Match reflects
      // OUR last write, so another admin's newer write still mismatches.
      try {
        const fresh = await apiClient.getTables()
        const map: Record<string, string> = {}
        for (const t of (fresh.data || []) as DiningTable[]) {
          if (t.id && t.updated_at) map[t.id] = t.updated_at
        }
        return map
      } catch {
        // Non-fatal: the save succeeded. Worst case the next save 409s once and
        // the operator reloads.
        return undefined
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] })
      queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
      toastHelpers.success('Layout saved', 'Table positions updated successfully.')
    },
    onError: (error: unknown) => {
      const code = (error as { code?: string })?.code
      if (code === 'version_mismatch') {
        toastHelpers.warning('Floor was edited elsewhere', (error as Error).message)
        // Force a fresh load so the operator sees the current truth.
        queryClient.invalidateQueries({ queryKey: ['tables'] })
        queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
        return
      }
      toastHelpers.apiError('Save layout', error)
    },
  })

  const floorMutation = useMutation({
    mutationFn: (updatedFloors: string[]) => apiClient.updateSetting('managed_floors', updatedFloors),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'managed_floors'] })
    },
    onError: (error: unknown) => {
      toastHelpers.apiError('Update floors', error)
    },
  })

  const handleCreateFloor = async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    if (effectiveFloors.some((f) => f.toLowerCase() === trimmed.toLowerCase())) return
    setPendingFloors((prev) => mergeFloorList(prev, [trimmed]))
    const next = mergeFloorList([...floors, trimmed], [])
    try {
      await floorMutation.mutateAsync(next)
      toastHelpers.success('Floor added', `${trimmed} is now available.`)
    } catch (error) {
      setPendingFloors((prev) => prev.filter((f) => f.toLowerCase() !== trimmed.toLowerCase()))
      throw error
    }
  }

  // Move every table on a floor to a new location, tolerating per-table
  // failures: a mid-batch error must not silently strand half the floor.
  // Returns table numbers that failed so callers can surface them and skip
  // any destructive follow-up (like removing the floor from settings).
  const moveFloorTables = async (tablesToMove: DiningTable[], location: string): Promise<string[]> => {
    const results = await Promise.allSettled(
      tablesToMove.map((t) => apiClient.updateTable(t.id, { location })),
    )
    return tablesToMove
      .filter((_, i) => {
        const r = results[i]
        return r.status === 'rejected' || !r.value.success
      })
      .map((t) => t.table_number)
  }

  const handleRenameFloor = async (from: string, to: string) => {
    const value = to.trim()
    if (!value || value === from) return
    const next = floors.map((f) => (f === from ? value : f))
    await floorMutation.mutateAsync(Array.from(new Set(next)))
    const touched = (allTables as DiningTable[]).filter((t) => (t.location || 'Main Floor') === from)
    const failed = await moveFloorTables(touched, value)
    invalidateTables(queryClient)
    setSelectedFloor(value)
    if (failed.length > 0) {
      toastHelpers.error(
        'Floor partially renamed',
        `${failed.length} table(s) still on "${from}": ${failed.join(', ')}. Retry the rename to move them.`,
      )
      return
    }
    toastHelpers.success('Floor renamed', `${from} renamed to ${value}.`)
  }

  const handleDeleteFloor = async (name: string, moveTo: string) => {
    if (!moveTo || moveTo === name) return
    const touched = (allTables as DiningTable[]).filter((t) => (t.location || 'Main Floor') === name)
    const failed = await moveFloorTables(touched, moveTo)
    if (failed.length > 0) {
      // Keep the floor: deleting it now would orphan the tables that failed
      // to move onto a floor that no longer exists.
      invalidateTables(queryClient)
      toastHelpers.error(
        'Floor not deleted',
        `${failed.length} table(s) could not be moved: ${failed.join(', ')}. The floor was kept — retry the delete.`,
      )
      return
    }
    const next = floors.filter((f) => f !== name)
    await floorMutation.mutateAsync(next)
    invalidateTables(queryClient)
    setSelectedFloor(moveTo)
    toastHelpers.success('Floor deleted', `${name} moved to ${moveTo}.`)
  }

  // Built once and rendered in both the floor-plan and table-list branches —
  // they return separately, and the print job is reachable from either. The
  // `tentsOpen &&` guard keeps the sheet unmounted while closed, so it does not
  // mint slugs for every table in the restaurant just by being on the page.
  const tentsDialog = (
    <Dialog open={tentsOpen} onOpenChange={setTentsOpen}>
      <DialogContent className="sm:max-w-2xl">
        {tentsOpen && (
          <TableTentSheet tables={allTables} onClose={() => setTentsOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  )

  /**
   * Per-table dine-in QR (F-FE-N100). Replaces the old ?table=<uuid> deep link:
   * the printed code carries a rotatable slug, so a leaked card can be
   * invalidated without the table's UUID ever appearing in a URL a customer
   * could edit.
   *
   * Held as an element and rendered in EVERY view branch, like tentsDialog.
   * This component returns early per view mode, so a dialog placed only in the
   * list branch is simply not mounted on the floor plan — the QR button there
   * set the state and nothing appeared, which is exactly what it looked like.
   */
  const qrDialog = (
    <Dialog open={!!qrTable} onOpenChange={(o) => !o && setQrTable(null)}>
      <DialogContent className="sm:max-w-md">
        {qrTable && <TableQrSheet table={qrTable} onClose={() => setQrTable(null)} />}
      </DialogContent>
    </Dialog>
  )

  if (viewMode === 'table-form') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Table Management</h2>
            <p className="text-muted-foreground">
              {editingTable ? 'Update table details' : 'Add a new table to the floor plan'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setViewMode('list')} className="gap-2">
              <List className="h-4 w-4" />
              Table List
            </Button>
            <Button variant="outline" onClick={() => setViewMode('layout')} className="gap-2">
              <LayoutGrid className="h-4 w-4" />
              Layout Builder
            </Button>
            <Button
              onClick={() => {
                setEditingTable(null)
                setViewMode('table-form')
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Table
            </Button>
          </div>
        </div>

        <TableForm
          table={editingTable || undefined}
          floors={effectiveFloors}
          onCreateFloor={handleCreateFloor}
          onRenameFloor={handleRenameFloor}
          onDeleteFloor={handleDeleteFloor}
          mode={editingTable ? 'edit' : 'create'}
          onSuccess={handleFormSuccess}
          onCancel={handleCancelForm}
        />
      </div>
    )
  }

  if (viewMode === 'layout') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-3xl font-bold tracking-tight">Table Management</h2>
            <p className="text-muted-foreground">Build a visual floor map and arrange table positions</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ViewSwitch value="layout" onChange={setViewMode} />
            <PrintTentsButton
              disabled={allTables.length === 0}
              onClick={() => setTentsOpen(true)}
            />
          </div>
        </div>

        <TableLayoutBuilder
          tables={allTables as DiningTable[]}
          // Reuses the one QR dialog this page already owns rather than giving
          // the builder its own, so the floor plan and the table list open the
          // identical sheet.
          onShowQr={(t) => setQrTable(t)}
          floors={effectiveFloors}
          floorLayouts={floorLayouts}
          onResizeFloor={handleResizeFloor}
          onDirtyChange={handleLayoutDirtyChange}
          selectedFloor={selectedFloor}
          onFloorChange={(name) => {
            setSelectedFloor(name)
          }}
          onSave={(payload) => saveLayoutMutation.mutateAsync(payload)}
          onCreateFloor={async (name) => {
            await handleCreateFloor(name)
            setSelectedFloor(name.trim())
          }}
          onRenameFloor={handleRenameFloor}
          onDeleteFloor={handleDeleteFloor}
          onUpsertTable={async (payload) => {
            try {
              if (payload.id) {
                const { ifMatch, ...body } = payload
                await apiClient.updateTable(payload.id, body, { ifMatch: ifMatch ?? null })
              } else {
                await apiClient.createTable(payload)
              }
              queryClient.invalidateQueries({ queryKey: ['tables'] })
              queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
              toastHelpers.success('Table saved', `${payload.table_number} updated.`)
            } catch (error) {
              const e = error as { status?: number; code?: string }
              if (e?.status === 409 && e?.code === 'version_mismatch') {
                toastHelpers.warning(
                  'Table was edited elsewhere',
                  'Someone else updated this table. Reloading the latest version.'
                )
                queryClient.invalidateQueries({ queryKey: ['tables'] })
                queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
                return
              }
              toastHelpers.apiError('Save table', error)
              throw error
            }
          }}
          onDeleteTable={async (id) => {
            await apiClient.deleteTable(id)
            queryClient.invalidateQueries({ queryKey: ['tables'] })
            queryClient.invalidateQueries({ queryKey: ['tables-summary'] })
            toastHelpers.success('Table deleted', 'Removed from floor and table list.')
          }}
        />

        {tentsDialog}
        {qrDialog}
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-3xl font-bold tracking-tight">Table Management</h2>
          <p className="text-muted-foreground">Manage your restaurant's dining tables and seating arrangements</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewSwitch value="list" onChange={setViewMode} />
          <PrintTentsButton
            disabled={allTables.length === 0}
            onClick={() => setTentsOpen(true)}
          />
          <Button
            onClick={() => {
              setEditingTable(null)
              setViewMode('table-form')
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Table
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-6 text-center"><div className="text-2xl font-bold">{stats.total}</div><p className="text-xs text-muted-foreground">Total Tables</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="text-2xl font-bold text-green-600">{stats.available}</div><p className="text-xs text-muted-foreground">Available</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="text-2xl font-bold text-blue-600">{stats.occupied}</div><p className="text-xs text-muted-foreground">Occupied</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="text-2xl font-bold text-muted-foreground">{effectiveFloors.length}</div><p className="text-xs text-muted-foreground">Floors / Areas</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="text-2xl font-bold text-muted-foreground">{filteredTables.length}</div><p className="text-xs text-muted-foreground">Filtered</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tables by number or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            All ({stats.total})
          </Button>
          <Button
            variant={filterStatus === 'available' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('available')}
          >
            Available ({stats.available})
          </Button>
          <Button
            variant={filterStatus === 'occupied' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('occupied')}
          >
            Occupied ({stats.occupied})
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card><CardContent className="pt-6">Loading tables...</CardContent></Card>
      ) : filteredTables.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Settings className="mx-auto h-12 w-12 text-muted-foreground/70" />
              <h3 className="mt-2 text-sm font-medium text-foreground">No tables found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchTerm || filterStatus !== 'all' ? 'No tables match your current filters.' : 'Get started by adding your first table.'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <div className="mt-6">
                  <Button onClick={() => setViewMode('table-form')} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Table
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTables.map((table) => {
            const occupied = table.has_active_order ?? table.is_occupied
            const statusMeta = getTableStatusMeta(occupied)
            const activityLabel = getTableActivityLabel(table, occupied)
            // Live turnover: how long this party has been seated, right now.
            const occStart = occupied ? occupancyStartOf(table) : null
            const elapsedMin = occStart != null ? minutesSince(occStart, now) : null
            const turnStyle =
              elapsedMin != null ? TURNOVER_STATUS_STYLES[turnoverStatus(elapsedMin)] : null
            return (
              <Card key={table.id} className={`hover:shadow-md transition-shadow border ${occupied ? 'bg-muted/60 border-border' : 'bg-card border-success-border'} ${turnStyle ? `border-l-4 ${turnStyle.border}` : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl leading-none tracking-tight">Table {table.table_number}</CardTitle>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge
                          variant="outline"
                          className={`pointer-events-none gap-1.5 border transition-none ${statusMeta.className}`}
                        >
                          <span className={`h-2 w-2 rounded-full ${statusMeta.dotClass}`} />
                          {occupied ? <Users className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                          {statusMeta.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1.5 text-lg font-medium text-muted-foreground">
                        <Users className="h-4 w-4" />
                        {table.seating_capacity} seats
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {table.location && (
                    <div className="flex items-start gap-2 mb-5">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{table.location}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-border/60">
                    {turnStyle && elapsedMin != null ? (
                      <div className={`flex items-center gap-1.5 text-sm font-semibold ${turnStyle.text}`}>
                        <span className={`h-2 w-2 rounded-full ${turnStyle.dot}`} />
                        Occupied {formatDuration(elapsedMin)}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground font-medium">
                        {activityLabel}
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQrTable(table)}
                        className="gap-2"
                        title="Dine-in QR code"
                      >
                        <QrCode className="h-4 w-4" />
                        QR
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTable(table)
                          setViewMode('table-form')
                        }}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteTable(table)}
                        disabled={deleteTableMutation.isPending || occupied}
                        className="gap-2 text-red-600 hover:text-danger-fg hover:border-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {qrDialog}

      {/* Print-all (F-FE-N100). Restores the print action the old TableQrDialog
          carried: without it, putting codes on forty tables meant forty PNG
          downloads and forty trips to the printer. */}
      {tentsDialog}

      <Dialog open={deleteTableOpen} onOpenChange={setDeleteTableOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Table?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete Table {pendingDeleteTable?.table_number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTableOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTable}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Entry point to the print-all sheet (F-FE-N100). Sits in both the floor-plan
// and table-list headers because either is a plausible place to be standing
// when you decide to put QR codes on the tables.
function PrintTentsButton({
  disabled,
  onClick,
}: {
  disabled: boolean
  onClick: () => void
}) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      className="gap-2"
      title="Print an A5 QR tent for every table"
    >
      <Printer className="h-4 w-4" />
      Print table QRs
    </Button>
  )
}

// Segmented Floor plan / Table list switcher. Replaces the pair of loose
// "Table List" / "Layout Builder" buttons with one enterprise-grade toggle that
// makes the current view unmistakable.
function ViewSwitch({
  value,
  onChange,
}: {
  value: 'layout' | 'list'
  onChange: (v: ViewMode) => void
}) {
  const items: Array<{ key: 'layout' | 'list'; label: string; icon: typeof LayoutGrid }> = [
    { key: 'layout', label: 'Floor plan', icon: LayoutGrid },
    { key: 'list', label: 'Table list', icon: List },
  ]
  return (
    <div className="inline-flex items-center rounded-lg border bg-muted/60 p-0.5">
      {items.map(({ key, label, icon: Icon }) => {
        const active = value === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={active}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

function getTableStatusMeta(occupied: boolean): { label: string; className: string; dotClass: string } {
  if (occupied) {
    return {
      label: 'occupied',
      className:
        'bg-muted text-foreground border-input hover:bg-muted hover:text-foreground hover:border-input',
      dotClass: 'bg-muted-foreground',
    }
  }

  return {
    label: 'available',
    className:
      'bg-success-surface text-success-fg border-success-border hover:bg-success-surface hover:text-success-fg hover:border-success-border',
    dotClass: 'bg-green-500',
  }
}

function getTableActivityLabel(table: DiningTable, occupied: boolean): string {
  const withCurrentOrder = table as DiningTable & { current_order?: { created_at?: string | null } }
  const openedAt = withCurrentOrder.current_order?.created_at

  if (occupied && openedAt) {
    return `Opened ${formatRelativeTime(openedAt)}`
  }

  if (occupied) {
    return 'Currently occupied'
  }

  if (table.last_booked_at) {
    return `Last booked ${formatRelativeTime(table.last_booked_at)}`
  }

  return 'Never booked'
}

function formatRelativeTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'recently'
  const diffMs = Date.now() - date.getTime()
  const diffMins = Math.max(1, Math.round(diffMs / 60000))

  if (diffMins < 60) {
    return `${diffMins}m ago`
  }

  const diffHours = Math.round(diffMins / 60)
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }

  const diffDays = Math.round(diffHours / 24)
  return `${diffDays}d ago`
}
